import { Modality } from '~/domain/dicom/modality';

/**
 * Intensity-threshold helpers for the threshold drawing modifier.
 *
 * The UI works in **display units** — Hounsfield Units (HU) for CT, SUVbw for
 * PET — while the image volume the engine clips against stores **raw** voxel
 * values (raw HU for CT; raw activity for PET, where SUVbw = raw × suvFactor).
 * These pure functions are the single, unit-tested boundary that converts the
 * clinician-facing display range into the raw range the engine compares voxels
 * against, and that supplies the modality-specific defaults / presets the UI
 * seeds from. They have no engine/React/Recoil dependencies.
 *
 * Only CT and PET are in scope. Other modalities (MR, …) carry arbitrary
 * intensity units with no clinical HU/SUV interpretation, so thresholding is
 * not offered for them.
 */

/** Intensity range expressed in display units (HU for CT, SUVbw for PET). */
export interface IntensityRange {
    /** Lower bound in display units. */
    lower: number;
    /** Upper bound in display units. */
    upper: number;
}

/** Intensity range expressed in the image's raw voxel scale. */
export interface RawIntensityRange {
    rawLower: number;
    rawUpper: number;
}

/**
 * Modalities for which intensity thresholding is supported (CT → HU,
 * PET → SUVbw). MR and other unitless modalities are excluded.
 */
export function isThresholdSupportedModality(
    modality: Modality | undefined
): boolean {
    return modality === Modality.CT || modality === Modality.PT;
}

/**
 * Convert a display-unit range into the image's raw voxel scale so the engine
 * can compare it directly against `floatPixelData`.
 *
 *  - CT: display unit IS the raw unit (HU), so the range passes through.
 *  - PET: display SUVbw = raw × `suvFactor`, so the raw bound is the display
 *    bound divided by `suvFactor`. When `suvFactor` is null/unavailable — or a
 *    non-finite / non-positive value that a malformed series can produce — the
 *    range cannot be expressed as a SUV threshold, so we return `null` rather
 *    than silently thresholding raw activity under a SUV label (or dividing by
 *    NaN and yielding a `{NaN, NaN}` band that wipes every stroke).
 *  - Other modalities: `null` (unsupported).
 *
 * The result is normalised so `rawLower <= rawUpper`; an inverted input range
 * (which would otherwise select nothing) is interpreted as the band between the
 * two values, not silently dropped. A usable `suvFactor` is finite and positive
 * (guarded here and in `computeSUVbwFactor`), so dividing preserves ordering.
 *
 * Returns `null` when the constraint cannot be expressed for this image — the
 * caller surfaces that as an inactive state.
 */
export function displayRangeToRaw(
    range: IntensityRange,
    modality: Modality | undefined,
    suvFactor: number | null
): RawIntensityRange | null {
    const lo = Math.min(range.lower, range.upper);
    const hi = Math.max(range.lower, range.upper);

    switch (modality) {
        case Modality.CT:
            return { rawLower: lo, rawUpper: hi };
        case Modality.PT: {
            // NaN defeats `=== null` and `<= 0`, so require a finite positive
            // factor explicitly.
            if (
                suvFactor === null ||
                !Number.isFinite(suvFactor) ||
                suvFactor <= 0
            ) {
                return null;
            }
            return { rawLower: lo / suvFactor, rawUpper: hi / suvFactor };
        }
        default:
            return null;
    }
}

/**
 * Modality-specific default display range used when the Threshold modifier is
 * first enabled for an image, clamped to the image's actual display range so
 * the seeded handles always sit on the slider.
 *
 *  - CT: soft-tissue-and-up band — from −500 HU (excludes air/lung) to the
 *    image maximum.
 *  - PET: the conventional SUVbw ≥ 2.5 lesion threshold, up to the image
 *    maximum.
 *
 * @param modality Image modality.
 * @param displayMin Image intensity minimum in display units.
 * @param displayMax Image intensity maximum in display units.
 */
export function modalityDefaultRange(
    modality: Modality | undefined,
    displayMin: number,
    displayMax: number
): IntensityRange {
    const lo = Math.min(displayMin, displayMax);
    const hi = Math.max(displayMin, displayMax);
    const clamp = (v: number) => Math.min(hi, Math.max(lo, v));

    switch (modality) {
        case Modality.CT:
            return { lower: clamp(-500), upper: hi };
        case Modality.PT:
            return { lower: clamp(2.5), upper: hi };
        default:
            return { lower: lo, upper: hi };
    }
}

/**
 * A quick-select intensity band. An undefined bound is open-ended and resolved
 * against the image's display range by {@link resolvePreset}: `lower ===
 * undefined` means "from the image minimum" (e.g. "≤ −900 HU"); `upper ===
 * undefined` means "up to the image maximum" (e.g. "≥ 200 HU").
 */
export interface ThresholdPreset {
    /** i18n message id for the preset label. */
    labelId: string;
    /** Lower bound in display units, or undefined for "from image minimum". */
    lower?: number;
    /** Upper bound in display units, or undefined for "to image maximum". */
    upper?: number;
}

/**
 * Clinically conventional preset bands per supported modality. CT bands are in
 * HU; PET bands are open-ended SUVbw lower cutoffs. Returns an empty list for
 * unsupported modalities.
 */
export function thresholdPresets(
    modality: Modality | undefined
): readonly ThresholdPreset[] {
    switch (modality) {
        case Modality.CT:
            return CT_PRESETS;
        case Modality.PT:
            return PT_PRESETS;
        default:
            return [];
    }
}

// One preset per common tissue category, using conventional HU windows.
// Open-ended bounds (Air/Lung upper cutoffs, Bone lower cutoff) resolve to the
// image min/max, matching the "≤ / ≥" phrasing of the category thresholds.
const CT_PRESETS: readonly ThresholdPreset[] = [
    { labelId: 'thresholdPresetCtAir', upper: -900 },
    { labelId: 'thresholdPresetCtLung', upper: -400 },
    { labelId: 'thresholdPresetCtSoftTissue', lower: 20, upper: 80 },
    { labelId: 'thresholdPresetCtBone', lower: 200 },
];

const PT_PRESETS: readonly ThresholdPreset[] = [
    { labelId: 'thresholdPresetPtSuv25', lower: 2.5 },
    { labelId: 'thresholdPresetPtSuv40', lower: 4.0 },
];

/**
 * Resolve a preset against an image's display range: fill open-ended bounds with
 * the image minimum (lower) / maximum (upper) and clamp both onto the image's
 * actual display range so the result is always a valid, on-slider selection.
 */
export function resolvePreset(
    preset: ThresholdPreset,
    displayMin: number,
    displayMax: number
): IntensityRange {
    const lo = Math.min(displayMin, displayMax);
    const hi = Math.max(displayMin, displayMax);
    const clamp = (v: number) => Math.min(hi, Math.max(lo, v));
    return {
        lower: clamp(preset.lower ?? lo),
        upper: clamp(preset.upper ?? hi),
    };
}
